/**
 * The records an answer cited — the phone's Sources card, at console scale.
 *
 * ## One line per record
 *
 * The console used to draw a `Card` per reference: title, a mono meta blob and
 * a multi-line snippet, three stacked blocks each. Eight of those is a page.
 * The phone draws one 32px line — thumb, kind, title, `site · when`, chevron —
 * and the snippet does not appear at all: a reader deciding WHICH record to
 * open needs to tell them apart, not to re-read the evidence. This console has
 * no thumbnail source (`AskLedReference.thumbnailUrl` was dead plumbing — no
 * document builder ever assigned it), so its own tile draws the kind's glyph
 * instead of the phone's photo — see `SourceTile`.
 *
 * ## Cited only
 *
 * `citedRecordsInOrder` drops references the answer did not actually cite, so this
 * card never lists a retrieval hit that went unused. Multiple references to
 * the same record (including chunks of one walk) share one row.
 * An answer citing nothing renders no card; the Ask view says so in its own
 * words instead.
 *
 * Rows keep the existing `ask-citation` test id — the console has asserted on
 * it since the first Ask tab, and renaming it would break those tests for a
 * reason that has nothing to do with what they pin.
 */

import { CITE_LABEL, CITE_LABEL_UNKNOWN, KindChip, KindGlyph, META, ROW_TITLE } from "../chrome";
import { C } from "../../tokens";
import type { AskReference } from "../../api";
import type { ConsoleItemKind } from "../../../server/projection/consoleItem";
import { citedRecordsInOrder } from "./askCitations";
import { citationTarget, type CitationTarget } from "./citationTarget";

/** `KindChip` is keyed by the console's ITEM vocabulary, which has no `walk`.
 *  A walk row therefore draws its own label rather than borrowing a kind it
 *  is not. */
function isItemKind(recordKind: string): recordKind is ConsoleItemKind {
  return recordKind === "log" || recordKind === "observation" || recordKind === "task" || recordKind === "punch";
}

/**
 * The Walks pin — verbatim from the sidebar's own "Walks" destination icon
 * (`Shell.tsx`'s `viewRows`). Reused rather than invented: a `walk` or an
 * unrecognised `recordKind` gets the same mark the console already uses
 * elsewhere to mean "a walk", instead of a second glyph nobody has seen.
 */
const WALK_ICON = {
  d1: "M12 21s7-6.4 7-11a7 7 0 1 0-14 0c0 4.6 7 11 7 11Z",
  d2: "M12 12.4a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8Z",
};

/**
 * The row's 32px leading tile.
 *
 * `AskLedReference.thumbnailUrl` used to be branched on here, but no document
 * builder in `askLed.service.ts` ever assigned it — the truthy branch was
 * unreachable, so every row drew a blank `C.faint` square beside a `KindChip`
 * that already states the kind. The field is gone from every layer now (see
 * the finding); this draws the kind's own glyph instead, which is never
 * empty.
 */
function SourceTile({ recordKind }: { recordKind: string }) {
  return (
    <span
      aria-hidden
      style={{
        width: 32,
        height: 32,
        borderRadius: 7,
        background: C.faint,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
      }}
    >
      {isItemKind(recordKind) ? (
        <KindGlyph kind={recordKind} size={15} />
      ) : (
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke={C.sub}
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d={WALK_ICON.d1} />
          <path d={WALK_ICON.d2} />
        </svg>
      )}
    </span>
  );
}

function SourceRow({
  reference,
  onOpen,
  href,
}: {
  reference: AskReference;
  onOpen: (target: CitationTarget) => void;
  /** The console URL for this row's destination, precomputed by
   *  `SourcesCard` from its own `hrefFor` — absent when the caller supplied
   *  none, or `citationTarget` returned `null`. */
  href?: string;
}) {
  const target = citationTarget(reference);

  const rowStyle: React.CSSProperties = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 0",
    textAlign: "left",
    borderBottom: `1px solid ${C.line}`,
  };

  const content = (
    <>
      <SourceTile recordKind={reference.recordKind} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {isItemKind(reference.recordKind) ? (
            <KindChip kind={reference.recordKind} />
          ) : reference.recordKind === "walk" ? (
            <span style={{ ...META, fontSize: 9.77, letterSpacing: "0.7px", color: C.sub, background: C.faint, padding: "3px 7px", borderRadius: 5, flexShrink: 0 }}>
              {CITE_LABEL.walk}
            </span>
          ) : (
            // A `recordKind` this console has never heard of — a backend
            // ahead of this deployment — or one entirely missing (a legacy
            // history row from before `recordKind` existed). Matches
            // `citationTarget`'s own fail-safe, and `CiteChip`'s: say
            // "Record" rather than guess "Walk". `CITE_LABEL_UNKNOWN` is the
            // one place that word is spelled.
            <span style={{ ...META, fontSize: 9.77, letterSpacing: "0.7px", color: C.sub, background: C.faint, padding: "3px 7px", borderRadius: 5, flexShrink: 0 }}>
              {CITE_LABEL_UNKNOWN}
            </span>
          )}
          <span style={{ ...ROW_TITLE, fontSize: 14.37, fontWeight: 600, color: target ? undefined : C.sub2 }}>
            {reference.title}
          </span>
        </span>
        <span style={{ ...META, display: "flex", flexWrap: "wrap", gap: 6, fontSize: 10.35, color: C.sub2, marginTop: 3 }}>
          {reference.siteName ? <span>{reference.siteName}</span> : null}
          {reference.siteName && reference.when ? <span aria-hidden>·</span> : null}
          {reference.when ? <span>{reference.whenLabel ?? "Created"} {reference.when}</span> : null}
          {reference.dueWhen && (reference.siteName || reference.when) ? <span aria-hidden>·</span> : null}
          {reference.dueWhen ? <span>Due {reference.dueWhen}</span> : null}
        </span>
      </span>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden style={{ flexShrink: 0 }}>
        <path d="m9 5 7 7-7 7" stroke={C.sub2} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </>
  );

  // No console destination — an unmappable `recordKind`, a `walk` citation
  // with no `visitId`, or a caller that supplied no `hrefFor` at all (`href`
  // absent). `CiteChip` already settled this: stay visible (the reader
  // should still see the record was cited) but render a plain, muted,
  // non-interactive element rather than a `disabled` button — the console's
  // global button reset has no `:disabled` rule, so a disabled button here
  // would be pixel-identical to a live one. Never an anchor with a dead href.
  // `aria-disabled` carries the same signal to a screen reader that the
  // opacity carries visually.
  if (!target || !href) {
    return (
      <span
        data-testid="ask-citation"
        aria-disabled="true"
        style={{ ...rowStyle, cursor: "default", opacity: 0.55 }}
      >
        {content}
      </span>
    );
  }

  return (
    <a
      data-testid="ask-citation"
      className="kb"
      href={href}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        onOpen(target);
      }}
      style={{ ...rowStyle, cursor: "pointer" }}
    >
      {content}
    </a>
  );
}

export function SourcesCard({
  text,
  references,
  onOpen,
  hrefFor,
}: {
  text: string;
  references: AskReference[];
  onOpen: (target: CitationTarget) => void;
  /** Builds the console URL for a row's destination, from `AskLed`'s own
   *  `hrefForItem`/`hrefForWalk`. Omit to keep every row a non-interactive
   *  `<span>` — `SourceRow` never renders an anchor without a real `href`. */
  hrefFor?: (target: CitationTarget) => string;
}) {
  const cited = citedRecordsInOrder(text, references);
  if (cited.length === 0) return null;

  return (
    <details
      data-testid="ask-sources"
      style={{
        marginTop: 13,
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        background: C.surface,
        overflow: "hidden",
      }}
    >
      <summary
        className="kb"
        data-testid="ask-used-records"
        style={{
          padding: "10px 13px",
          background: C.faint,
          fontSize: 14,
          lineHeight: 1.5,
          color: C.ink,
          cursor: "pointer",
        }}
      >
        View the {cited.length.toLocaleString("en-US")} {cited.length === 1 ? "record" : "records"} used in this answer
      </summary>
      <div style={{ padding: "0 13px" }}>
        {cited.map((reference) => {
          const target = citationTarget(reference);
          return (
            <SourceRow
              key={reference.id}
              reference={reference}
              onOpen={onOpen}
              href={target && hrefFor ? hrefFor(target) : undefined}
            />
          );
        })}
      </div>
    </details>
  );
}
